import type { PriceLineResponse, SalesChannelResponse } from '../model/types';
import { isBaseSlot } from './priceSlots';

/**
 * Mirror chains are cycle-guarded and capped at the same depth as the price engine's own
 * resolution (`PriceResolutionService.MAX_MIRROR_DEPTH`) — see adr-pricing-005. Kept in lockstep
 * with the backend constant; bump both together if it ever changes.
 */
const MAX_MIRROR_DEPTH = 8;

// ── Grid scope value ↔ string encoding (the "Prices for:" selector — design spec §8.5) ──────────
// Lives here (not in `PriceGridPanel.tsx`) purely so that file only exports the component itself
// — co-locating a plain type/function export there trips `react-refresh/only-export-components`.

export const BASE_SCOPE = 'base';

export type GridScope = { kind: 'base' } | { kind: 'channel'; code: string } | { kind: 'list'; code: string };

export function encodeScope(scope: GridScope): string {
  if (scope.kind === 'base') return BASE_SCOPE;
  return `${scope.kind}:${scope.code}`;
}

export function decodeScope(value: string): GridScope {
  if (value === BASE_SCOPE) return { kind: 'base' };
  const [kind, ...rest] = value.split(':');
  const code = rest.join(':');
  if (kind === 'channel' && code) return { kind: 'channel', code };
  if (kind === 'list' && code) return { kind: 'list', code };
  return { kind: 'base' };
}

/**
 * Which rung of the adr-pricing-009 specificity ladder actually supplied the cell's price —
 * `channelGroup` (channel+group exact match) is the most specific, `base` the least. Drives the
 * grid's inherited-prefix label ("= channel" / "= group" / "= base") once a customer-group axis is
 * in play (design spec §8.5/§8.10, plan-platform-002 WP4.3); a mirror hit is reported via
 * `mirroredFromChannelCode` instead (a provenance label, not a rung name).
 */
export type MatchedRung = 'channelGroup' | 'channel' | 'group' | 'base';

export interface GridCellResolution {
  line: PriceLineResponse;
  inherited: boolean;
  /**
   * Set when the effective price was inherited through a channel-mirror hop (adr-pricing-005)
   * rather than the base line — the channel code whose own price line is actually in effect, so
   * the UI can render an accurate "= OWN_ONLINE" provenance label instead of a misleading
   * "= base" for a channel that mirrors another channel's price.
   */
  mirroredFromChannelCode?: string;
  /** Absent only for the pre-WP4.3 call sites that never look at it; every path below sets it. */
  matchedRung?: MatchedRung;
}

/**
 * Returns true when the line's state is ACTIVE and the current time falls
 * within the half-open validity window [validFrom, validTo).
 * Missing bounds are treated as open (no lower/upper limit).
 */
function isWindowActive(line: PriceLineResponse, now: Date): boolean {
  if (line.state !== 'ACTIVE') return false;
  if (line.validFrom && now < new Date(line.validFrom)) return false;
  if (line.validTo && now >= new Date(line.validTo)) return false;
  return true;
}

/**
 * Resolves the mirror-terminal channel for `channelCode`, mirroring the backend
 * `PriceResolutionService.resolveEffectiveChannel` (adr-pricing-005): a channel with
 * `mirrorsChannelCode` set NEVER serves its own line — the effective identity is resolved
 * UNCONDITIONALLY and FIRST, before any line lookup happens, and the full ladder below runs
 * against that effective identity only. Each hop must land on a currently registered, ACTIVE
 * channel (kept as shipped — the backend's own walk only checks the channel is registered; the
 * grid additionally requires it active, since a deactivated channel is not a trustworthy price
 * source here either); an absent/deactivated target stops the walk and the LAST successfully
 * resolved identity stands (no substitution without a validated target) — which, on the very
 * first hop, is `channelCode` itself. A visited-set guards a cycle (A mirrors B mirrors A);
 * capped at `MAX_MIRROR_DEPTH` hops, matching `PriceResolutionService.MAX_MIRROR_DEPTH`.
 *
 * Returns `channelCode` unchanged when it does not mirror anything (the common case) — including
 * when `channels` doesn't carry it at all (pre-WP4.3 call sites that omit `channels` entirely).
 */
function resolveEffectiveChannel(channelCode: string, channels: SalesChannelResponse[]): string {
  const byCode = new Map(channels.map((c) => [c.code, c] as const));
  let current = channelCode;
  const visited = new Set<string>();

  for (let hop = 0; hop < MAX_MIRROR_DEPTH; hop += 1) {
    if (visited.has(current)) break; // cycle back to an already-visited channel
    visited.add(current);

    const mirrorsTo = byCode.get(current)?.mirrorsChannelCode;
    if (!mirrorsTo) return current; // terminal: unknown channel, or doesn't mirror anyone

    const target = byCode.get(mirrorsTo);
    if (!target?.active) return current; // absent (deleted/renamed) or deactivated mirror target

    current = mirrorsTo;
  }

  return current;
}

/**
 * Among lines with state === 'ACTIVE' whose validity window covers `now` (half-open [validFrom, validTo)),
 * find the line for `channelCode` (null = base line where channelCode is null/undefined), composed
 * with the optional `customerGroupCode` axis (adr-pricing-009, plan-platform-002 WP4.3) — the
 * 4-rung specificity ladder `channel+group → channel → group → base`, walked against the
 * MIRROR-EFFECTIVE channel (`resolveEffectiveChannel`, adr-pricing-005), exactly like
 * `PriceResolutionService.doResolve` walks `ladderSlots(effectiveChannel, customerGroupCode)`.
 *
 * Resolution:
 *  1. Resolve `channelCode`'s effective identity ONCE, unconditionally, up front. A channel with
 *     `mirrorsChannelCode` set never contributes its own line — not even when one exists — because
 *     every rung below is tried against the effective channel, never the requested one.
 *  2. **channel+group** — an exact match on both dimensions (effective channel + group), when both apply.
 *  3. **channel** — the effective channel's own group-less line.
 *  4. **group** — a group-scoped, channel-less line, when a group is given.
 *  5. **base** — both-null.
 *
 * `inherited` is true whenever the winning rung is LESS specific than what the caller actually
 * asked for (e.g. asking for channel+group but only a plain channel or base line exists), OR a
 * mirror hop actually happened — a channel that borrows another's price never gets to call the
 * result "its own", no matter which rung ultimately supplied it (this is what unchanged pre-WP4.3
 * behaviour degrades to when `customerGroupCode` is omitted: asking for a channel and landing on
 * `base` is still `inherited: true`, asking for `base` and finding it is `inherited: false`,
 * exactly as before). `mirroredFromChannelCode` is set to the effective channel whenever it
 * differs from the requested one, regardless of which rung actually supplied the line — it names
 * *whose* ladder produced this cell, not which specific rung matched.
 *
 * Returns null when nothing at all resolves.
 */
export function resolveGridCell(
  lines: PriceLineResponse[],
  channelCode: string | null,
  now: Date,
  channels: SalesChannelResponse[] = [],
  customerGroupCode: string | null = null,
): GridCellResolution | null {
  // adr-pricing-025 — `lines` (from `GET .../price-lines?productId=`) now also carries STATIC
  // price-list lines (a `priceListCode`, no `channelCode`). Those must never be mistaken for the
  // base (all-channels) line here, or a list-scoped line would silently leak into the Base/Channel
  // grid scopes. List-mode resolution is `resolveListGridCell`, not this function.
  const active = lines.filter((l) => isWindowActive(l, now) && !l.priceListCode);
  // `isBaseSlot` (design spec §8.0, `lib/priceSlots.ts`) is the single source of truth for "is
  // this line the base line" — imported rather than re-declared here so the R0 defect (a
  // list/group line mistaken for base) can never reappear in two places at once.
  const baseLine = active.find(isBaseSlot);

  const effectiveChannel = channelCode ? resolveEffectiveChannel(channelCode, channels) : null;
  const mirrored = effectiveChannel !== null && effectiveChannel !== channelCode;

  const mostSpecific: MatchedRung = effectiveChannel
    ? customerGroupCode
      ? 'channelGroup'
      : 'channel'
    : customerGroupCode
      ? 'group'
      : 'base';

  const direct = (
    rung: MatchedRung,
    line: PriceLineResponse | undefined,
  ): GridCellResolution | null => {
    if (!line) return null;
    const resolution: GridCellResolution = { line, inherited: mirrored || rung !== mostSpecific, matchedRung: rung };
    if (mirrored && effectiveChannel) resolution.mirroredFromChannelCode = effectiveChannel;
    return resolution;
  };

  if (effectiveChannel && customerGroupCode) {
    const exact = active.find(
      (l) => l.channelCode === effectiveChannel && l.customerGroupCode === customerGroupCode,
    );
    if (exact) return direct('channelGroup', exact);
  }

  if (effectiveChannel) {
    const ownLine = active.find((l) => l.channelCode === effectiveChannel && !l.customerGroupCode);
    if (ownLine) return direct('channel', ownLine);
  }

  if (customerGroupCode) {
    const groupLine = active.find((l) => !l.channelCode && l.customerGroupCode === customerGroupCode);
    if (groupLine) return direct('group', groupLine);
  }

  return direct('base', baseLine);
}

/**
 * List-mode grid cell resolution (design spec §8.5) — a price-list line never inherits or
 * mirrors: a product either has an in-window ACTIVE line in THIS list, or it falls through to
 * channel/base (rendered by the caller as a fallback caption, not resolved by this function).
 */
export function resolveListGridCell(
  lines: PriceLineResponse[],
  priceListCode: string,
  now: Date,
): PriceLineResponse | null {
  return lines.find((l) => l.priceListCode === priceListCode && isWindowActive(l, now)) ?? null;
}

/** Returns the amount of the tier whose minQuantity exactly matches `minQuantity`, or undefined. */
export function tierAmount(line: PriceLineResponse, minQuantity: number): number | undefined {
  return line.tiers.find((t) => t.minQuantity === minQuantity)?.amount;
}

/** The unit amount in effect at `quantity`: the amount of the greatest tier with minQuantity <= quantity. */
export function effectiveTierAmount(line: PriceLineResponse, quantity: number): number | undefined {
  const applicable = (line.tiers ?? [])
    .filter((t) => t.minQuantity <= quantity)
    .sort((a, b) => b.minQuantity - a.minQuantity);
  return applicable[0]?.amount;
}

/**
 * Data-driven tier columns (design spec R5 fix, plan-platform-002 WP4.3) — the ascending union of
 * `minQuantity` across the given lines, always including `1` so the base unit-price column never
 * disappears even when every visible line has a single flat tier. Replaces the hardcoded
 * `[1, 10, 100]` tier columns both the price grid (`PriceGridPanel`) and the price-list detail
 * page's lines table (`PriceListDetailPage`) used to render regardless of what the data contained.
 */
export function deriveTierColumns(lines: (PriceLineResponse | null | undefined)[]): number[] {
  const quantities = new Set<number>([1]);
  for (const line of lines) {
    for (const tier of line?.tiers ?? []) {
      quantities.add(tier.minQuantity);
    }
  }
  return Array.from(quantities).sort((a, b) => a - b);
}
